import { useState } from 'react';
import {
  ScheduleChangeRequest,
  respondScheduleChangeRequest,
} from '../../../lib/inspections';
import { useToastStore } from '../../../stores/toast';
import Modal from '../../Modal';
import { subjectParticleOf } from '@safetyhub/shared';
import { Badge, Button, Card, type BadgeTone } from '@safetyhub/ui';

interface ScheduleChangeRequestPanelProps {
  inspectionId: string;
  requests: ScheduleChangeRequest[];
  /** Owner 여부 (승인/거부 버튼 표시) */
  isOwner: boolean;
  onResponded?: () => void;
}

const REQUEST_TYPE_LABEL: Record<string, string> = {
  RESCHEDULE: '날짜 변경',
  CANCEL: '점검 취소',
};

const STATUS_LABEL: Record<string, string> = {
  PENDING: '검토 중',
  APPROVED: '승인됨',
  REJECTED: '거부됨',
};

const STATUS_TONE: Record<string, BadgeTone> = {
  PENDING: 'amber',
  APPROVED: 'emerald',
  REJECTED: 'rose',
};

function formatDate(isoStr?: string | null): string {
  if (!isoStr) return '-';
  return new Date(isoStr).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function ScheduleChangeRequestPanel({
  inspectionId,
  requests,
  isOwner,
  onResponded,
}: ScheduleChangeRequestPanelProps) {
  const { showToast } = useToastStore();
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [respondingRequestId, setRespondingRequestId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const pendingRequest = requests.find((r) => r.status === 'PENDING');
  const historyRequests = requests.filter((r) => r.status !== 'PENDING');

  const handleApprove = async (requestId: string) => {
    setIsSubmitting(true);
    try {
      const res = await respondScheduleChangeRequest(inspectionId, requestId, { approve: true });
      if (res.success) {
        const req = requests.find((r) => r.id === requestId);
        const msg = req?.requestType === 'CANCEL'
          ? '취소 요청이 승인되었습니다. 점검이 삭제됩니다.'
          : '날짜 변경 요청이 승인되었습니다.';
        showToast(msg, 'success');
        onResponded?.();
      } else {
        showToast('처리에 실패했습니다.', 'error');
      }
    } catch {
      showToast('처리에 실패했습니다.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenRejectModal = (requestId: string) => {
    setRespondingRequestId(requestId);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const handleReject = async () => {
    if (!respondingRequestId) return;
    setIsSubmitting(true);
    try {
      const res = await respondScheduleChangeRequest(inspectionId, respondingRequestId, {
        approve: false,
        rejectReason: rejectReason.trim() || undefined,
      });
      if (res.success) {
        showToast('요청이 거부되었습니다.', 'success');
        setRejectModalOpen(false);
        onResponded?.();
      } else {
        showToast('처리에 실패했습니다.', 'error');
      }
    } catch {
      showToast('처리에 실패했습니다.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (requests.length === 0) return null;

  return (
    <div className="space-y-3">
      {/* PENDING 요청 배너 */}
      {pendingRequest && (
        <div className="bg-amber-50 border border-amber-200 rounded-card px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge tone="amber" shape="rounded">
                  변경 요청 검토 중
                </Badge>
                <span className="text-xs font-medium text-amber-800">
                  {REQUEST_TYPE_LABEL[pendingRequest.requestType]}
                </span>
              </div>
              <p className="text-sm text-amber-900">
                <span className="font-medium">{pendingRequest.requestedBy?.name ?? '-'}</span>
                {subjectParticleOf(pendingRequest.requestedBy?.name ?? '-')}{' '}
                {pendingRequest.requestType === 'RESCHEDULE'
                  ? `${formatDate(pendingRequest.requestedDate)}로 변경을 요청했습니다.`
                  : '점검 취소를 요청했습니다.'}
              </p>
              {pendingRequest.reason && (
                <p className="text-xs text-amber-700 mt-1">사유: {pendingRequest.reason}</p>
              )}
              <p className="text-xs text-amber-600 mt-1">{formatDate(pendingRequest.createdAt)} 요청</p>
            </div>

            {/* Owner용 승인/거부 버튼 */}
            {isOwner && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  type="button"
                  size="sm"
                  onClick={() => handleOpenRejectModal(pendingRequest.id)}
                  disabled={isSubmitting}
                >
                  거부
                </Button>
                <Button
                  type="button"
                  variant="success"
                  size="sm"
                  onClick={() => handleApprove(pendingRequest.id)}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? '처리 중...' : '승인'}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 이력 (접기/펼치기) */}
      {historyRequests.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowHistory((v) => !v)}
            className="flex items-center gap-1.5 text-xs text-ink-500 hover:text-ink-700 transition-colors"
          >
            <svg
              className={`w-3.5 h-3.5 transition-transform ${showHistory ? 'rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            변경 요청 이력 ({historyRequests.length}건)
          </button>

          {showHistory && (
            <div className="mt-2 space-y-2">
              {historyRequests.map((req) => (
                <Card
                  key={req.id}
                  padding="none"
                  elevation="none"
                  className="px-4 py-3 text-sm"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-ink-700">
                        {REQUEST_TYPE_LABEL[req.requestType]}
                      </span>
                      <Badge tone={STATUS_TONE[req.status]} shape="rounded">
                        {STATUS_LABEL[req.status]}
                      </Badge>
                    </div>
                    <span className="text-xs text-ink-400">{formatDate(req.createdAt)}</span>
                  </div>

                  <div className="space-y-1 text-xs text-ink-600">
                    <p>요청자: {req.requestedBy?.name ?? '-'}</p>
                    {req.requestType === 'RESCHEDULE' && req.requestedDate && (
                      <p>희망일: {formatDate(req.requestedDate)}</p>
                    )}
                    {req.reason && <p>사유: {req.reason}</p>}
                    {req.status !== 'PENDING' && (
                      <>
                        <p>처리자: {req.respondedBy?.name ?? '-'}</p>
                        <p>처리일: {formatDate(req.respondedAt)}</p>
                        {req.status === 'REJECTED' && req.rejectReason && (
                          <p className="text-red-600">거부 사유: {req.rejectReason}</p>
                        )}
                      </>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 거부 사유 입력 모달 */}
      <Modal
        isOpen={rejectModalOpen}
        onClose={() => !isSubmitting && setRejectModalOpen(false)}
        title="변경 요청 거부"
        description="거부 사유를 입력해주세요. (선택사항)"
        size="sm"
        preventClose={isSubmitting}
        footer={
          <>
            <Button
              type="button"
              onClick={() => setRejectModalOpen(false)}
              disabled={isSubmitting}
            >
              취소
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={handleReject}
              disabled={isSubmitting}
            >
              {isSubmitting ? '처리 중...' : '거부 확인'}
            </Button>
          </>
        }
      >
        <div>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="거부 사유를 입력하면 안전관리자에게 전달됩니다."
            rows={3}
            maxLength={300}
            className="w-full border border-ink-300 rounded-control px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          />
          <p className="text-xs text-ink-400 text-right mt-1">{rejectReason.length}/300</p>
        </div>
      </Modal>
    </div>
  );
}
